// v1.22.0 · Auditoría · Conciliaciones automáticas del mayor contra los soportes del expediente
import React, { useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { C, SEV, money, plab } from './api';
import { DiffBars } from './charts';

const COLS = {
  banco: [['libros', 'Saldo libros'], ['soporte', 'Saldo banco'], ['diferencia', 'Diferencia'], ['debe_libros', 'Debe libros'], ['creditos_banco', 'Créditos banco'],
    ['dif_creditos', 'Dif.'], ['haber_libros', 'Haber libros'], ['debitos_banco', 'Débitos banco'], ['dif_debitos', 'Dif.']],
  sri_104: [['libros', 'Libros'], ['soporte', 'Form. 104 (799)'], ['diferencia', 'Diferencia'], ['ret_30', '30 %'], ['ret_70', '70 %'], ['ret_100', '100 %']],
  sri_103: [['libros', 'Libros'], ['soporte', 'Form. 103'], ['diferencia', 'Diferencia'], ['tipo', 'Declaración']],
  iess_individual: [['libros', 'Libros'], ['soporte', 'Planillas'], ['diferencia', 'Diferencia'], ['individual', 'Individual'], ['patronal', 'Patronal'], ['afiliados', 'Afiliados']],
  tarjeta: [['libros', 'Libros'], ['soporte', 'Total a pagar'], ['diferencia', 'Diferencia'], ['consumos', 'Consumos'], ['exterior', 'Exterior'], ['isd', 'ISD']],
  cxc: [['cuenta', 'Cuenta'], ['libros', 'Mayor'], ['soporte', 'Reporte del sistema'], ['diferencia', 'Diferencia'], ['dif_sistema', 'Dif. sistema'], ['sin_asiento', 'Sin asiento']],
  anexo: [['cuenta', 'Cuenta'], ['libros', 'Libros'], ['soporte', 'Anexo'], ['diferencia', 'Diferencia']],
};
const DEF = [['libros', 'Libros'], ['soporte', 'Soporte'], ['diferencia', 'Diferencia']];

export default function Conciliaciones({ analysis, goTab }) {
  const cs = analysis?.conciliations || [];
  const bad = cs.filter((c) => c.status === 'diferencias').length;
  if (!cs.length) {
    return (
      <div className="neu-card neu-empty">
        No hay conciliaciones todavía. Suba al expediente los estados de cuenta bancarios y de tarjeta, los formularios 103/104,
        las planillas del IESS, las carteras por cobrar y por pagar o anexos con código de cuenta: el sistema los cruza solo con el mayor.
        <div style={{ marginTop: 12 }}><button type="button" className="neu-btn neu-btn-primary" onClick={() => goTab('expediente')}>Ir al expediente</button></div>
      </div>
    );
  }
  return (
    <div className="neu-grid" style={{ gap: 18 }}>
      <section className="neu-card">
        <p className="neu-title" style={{ fontSize: 15 }}>Conciliaciones automáticas</p>
        <p className="neu-sub">{cs.length} conciliación(es) · {bad} con diferencias. Diferencia = libros − soporte; en rojo lo que no cuadra.</p>
        <div className="neu-grid neu-grid-3" style={{ marginTop: 14, gap: 12 }}>
          {cs.map((c) => (
            <a key={c.key} href={`#conc-${c.key}`} className="neu-card-flat" style={{ textDecoration: 'none', color: C.ink, display: 'flex', gap: 10, alignItems: 'center' }}>
              {c.status === 'conciliado' ? <CheckCircle2 size={20} color={C.good} /> : <AlertTriangle size={20} color={c.status === 'diferencias' ? SEV.alta.color : C.muted} />}
              <div style={{ minWidth: 0 }}><div style={{ fontWeight: 700, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>
                <div className="neu-hint">{c.status === 'diferencias' ? `Dif. máxima ${money(c.max_diff)}` : c.status === 'conciliado' ? 'Conciliado' : 'Sin datos'}</div></div>
            </a>
          ))}
        </div>
      </section>
      {cs.map((c) => <Conc key={c.key} c={c} />)}
    </div>
  );
}

function Conc({ c }) {
  const [open, setOpen] = useState(c.status === 'diferencias');
  const cols = COLS[c.type] || DEF;
  const labelKey = ['cxc', 'anexo'].includes(c.type) ? 'cuenta' : 'period';
  return (
    <section id={`conc-${c.key}`} className="neu-card">
      <div className="neu-row">
        <div style={{ flex: 1, minWidth: 220 }}>
          <p className="neu-title">{c.title}</p>
          <p className="neu-sub">{c.note}</p>
          {c.sources?.length > 0 && <p className="neu-hint" style={{ fontSize: 11 }}>Soportes: {c.sources.join(' · ')}</p>}
        </div>
        <span className={`neu-chip ${c.status === 'conciliado' ? 'neu-chip-green' : c.status === 'diferencias' ? 'neu-chip-red' : 'neu-chip-gray'}`}>
          {c.status === 'conciliado' ? '✓ Conciliado' : c.status === 'diferencias' ? `▲ Diferencias · máx. ${money(c.max_diff)}` : 'Sin datos'}</span>
        <button type="button" className="neu-btn neu-icon-btn" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? 'Ocultar detalle' : 'Ver detalle'}>{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>
      </div>
      {open && (
        <div className="neu-grid neu-grid-2" style={{ marginTop: 12 }}>
          <div className="neu-scroll" style={{ maxHeight: 360 }}>
            <table className="neu-table">
              <thead><tr>{labelKey === 'period' && <th>Mes</th>}{cols.map(([k, l], i) => <th key={k + i} className={k === 'cuenta' || k === 'tipo' ? '' : 'num'}>{l}</th>)}</tr></thead>
              <tbody>{c.rows.map((r, i) => (
                <tr key={i}>{labelKey === 'period' && <td style={{ whiteSpace: 'nowrap' }}>{plab(r.period)}</td>}
                  {cols.map(([k], j) => {
                    const v = r[k];
                    const diff = k.startsWith('dif');
                    return <td key={k + j} className={typeof v === 'number' ? 'num' : ''} style={diff && typeof v === 'number' && Math.abs(v) > 1 ? { color: SEV.alta.color, fontWeight: 700 } : undefined}>
                      {typeof v === 'number' ? money(v) : v ?? '—'}</td>;
                  })}</tr>
              ))}</tbody>
            </table>
          </div>
          <div>
            <DiffBars rows={c.rows} labelKey={labelKey} />
            {c.type === 'cxp' && c.antiguedad && <Aging data={c.antiguedad} years={c.por_anio} top={c.top} />}
            {c.type === 'tarjeta' && c.exterior_por_comercio && Object.keys(c.exterior_por_comercio).length > 0 && (
              <div style={{ marginTop: 12 }}><p className="neu-title">Consumos en el exterior por comercio ({money(c.exterior_total)})</p>
                <Bars data={c.exterior_por_comercio} color={C.s2} /></div>
            )}
            {c.type === 'sri_104' && c.credito_tributario?.some((r) => r.compras_con_credito_500 > 0 && !r.ventas_gravadas_401) && (
              <p className="neu-inset neu-hint" style={{ marginTop: 12 }}>
                <b style={{ color: SEV.alta.color }}>▲ Crédito tributario:</b> se declararon compras en el casillero 500 (con derecho a crédito) sin ventas gravadas (casillero 401).
              </p>
            )}
            {c.type === 'cxc' && c.total_cartera !== undefined && (
              <p className="neu-hint" style={{ marginTop: 10 }}>Total cartera {money(c.total_cartera)} · total contabilidad (todas las cuentas) {money(c.total_contabilidad_todas)}.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Bars({ data, color }) {
  const entries = Object.entries(data);
  const max = Math.max(...entries.map(([, v]) => Math.abs(v)), 1);
  return (
    <div className="neu-grid" style={{ gap: 6, marginTop: 6 }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 1fr) 2fr auto', gap: 8, alignItems: 'center', fontSize: 12 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k}>{k}</span>
          <div className="neu-inset" style={{ padding: 3, borderRadius: 8 }}><div style={{ width: `${(Math.abs(v) / max) * 100}%`, height: 10, borderRadius: 5, background: color }} /></div>
          <span className="tabular" style={{ fontWeight: 700 }}>{money(v, 0)}</span>
        </div>
      ))}
    </div>
  );
}

function Aging({ data, years, top }) {
  return (
    <div style={{ marginTop: 12 }}>
      <p className="neu-title">Antigüedad de las facturas por pagar</p>
      <Bars data={data} color={C.s2} />
      {years && <p className="neu-hint" style={{ marginTop: 6 }}>Por año de emisión: {Object.entries(years).sort().map(([y, v]) => `${y}: ${money(v, 0)}`).join(' · ')}</p>}
      {top?.length > 0 && <><p className="neu-title" style={{ marginTop: 10 }}>Principales proveedores</p><Bars data={Object.fromEntries(top.slice(0, 6).map((t) => [t.nombre, t.valor]))} color={C.s1} /></>}
    </div>
  );
}
